'use client';

//React or NextJS Imports
import React, { FC, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

//Library Imports
import { debounce } from 'lodash';
import Select from 'react-select';

//Hooks Imports
import { useAuthContext } from '@/src/v2/layout/Auth/context/AuthContext';
import { useFormContext } from 'react-hook-form';
import { useAppDispatch } from '@/src/v2/hooks/useAppDispatch/UseAppDispatch';
import { setIsLogging } from '@/src/v2/store/slices/user/UserSlice';

//Components Imports
import ControlledSelect from '@/src/components/clean/Form/ControlledSelect/ControlledSelect';
import KlicInputAutocomplete from '@/src/components/clean/InputAutocomplete/InputAutocomplete';
import { DropdownOption } from '../../AdminLayout/ManageUsers/manageUsersList';
import SelectStyles from '@/src/v2/components/reactSelect';
import ControlledCheckbox from '@/src/v2/components/Form/ControlledCheckbox/ControlledCheckbox';
import TermsAndConditionsModal from './TermsAndConditions/TermsAndConditions';

//GraphQl Imports
import { CheckEmail, CheckUsername } from '@/src/v2/service/v2/graphql/apiv2';

//Other Imports
import { SignUpSchema } from '@/src/v2/layout/Auth/SignUpModal/SignUpSchema';
import { signUpService, socialAuthService } from '@/src/services/auth';
import { Countries } from '@/src/v2/utils/constants/countries';
import {
  DayList,
  MonthList,
} from '@/src/utils/dropdown-options/DropDownOptions';

//Dynamic Imports
const Button = dynamic(() => import('@/src/v2/components/Button/Button'), {
  ssr: false,
});
const Icon = dynamic(() => import('@/src/v2/components/Icon'), { ssr: false });
const ControlledInput = dynamic(
  () => import('@/src/v2/components/Form/ControlledInput/ControlledInput'),
  { ssr: false }
);
const ControlledForm = dynamic(
  () => import('@/src/components/clean/Form/ControlledForm/ControlledForm'),
  { ssr: false }
);

//Style Imports
import style from './sign-up-modal.module.scss';
import { isMobile } from 'react-device-detect';
import { clear } from 'console';

type Props = {
  onClose: () => void;
};

const SignUpForm: FC<Props> = ({ onClose }) => {
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { setMode, setEmail, setUsername, setPassword } = useAuthContext();
  const {
    formState: { isValid, dirtyFields, errors },
    watch,
    reset,
    getValues,
    setError,
    handleSubmit,
    setError: setFieldError,
    setValue,
    clearErrors,
  } = useFormContext();

  const [password, email, username, country, termsOfUse] = watch([
    'password',
    'email',
    'username',
    'country',
    'termsOfUse',
  ]);

  const allCountries = Object.entries(Countries).map(([key, value]) => ({
    label: value,
    value,
  }));

  const [yearList, setYearList] = useState([]);
  useEffect(() => {
    for (let i = Number(new Date().getFullYear()); i >= 1944; i--) {
      setYearList((prevState) => [...prevState, { label: i, value: i }]);
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [localIsValid, setLocalIsValid] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(undefined);
  const [birthday, setBirthday] = useState<string>(undefined);
  const [countries, setCountries] = useState<DropdownOption[]>(allCountries);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] =
    useState(false);
  const [birthdayMessage, setBirthdayMessage] = useState('');

  const [selectedMonth, setSelectedMonth] = useState<any>([]);
  const [selectedDay, setSelectedDay] = useState<any>([]);
  const [selectedYear, setSelectedYear] = useState<any>([]);

  const { setCountry, setBirthDate } = useAuthContext();

  const searchCountries = debounce((inputValue: string) => {
    setCountries(
      allCountries.filter((item: DropdownOption) =>
        item.value.toLowerCase().includes(inputValue.toLowerCase())
      )
    );
  }, 1000);

  useEffect(() => {
    if (!errors['email'] && !errors['username'] && isValid) {
      setTimeout(() => setLocalIsValid(true), 1000);
      return;
    }
    setLocalIsValid(false);
  }, [errors, isValid]);

  const whenClosing = () => {
    reset();
    onClose();
  };

  const next = async () => {
    await new Promise((resolve, reject) => {
      handleSubmit(
        (data: any) => {
          setEmail(data.email);
          setUsername(data.username);
          setPassword(data.password);
          setMode('add country');
          resolve(Promise.resolve());
        },
        (err) => {
          reject(err);
        }
      )();
    });
  };

  const checkEmail = (emailValue: string) => {
    CheckEmail({ email: emailValue }).then((response) => {
      const isTaken = response.checkEmailV2?.isTaken;
      if (isTaken) {
        setFieldError('email', { message: 'Email Already Exists!' });
        return;
      } else {
        clearErrors('email');
      }
    });
  };

  const checkUserName = (usernameValue: string) => {
    if (usernameValue) {
      CheckUsername({ username: usernameValue }).then((response) => {
        const isTaken = response.checkUsernameV2?.isTaken;
        if (isTaken) {
          setFieldError('username', { message: 'Username is Already Taken!' });
          return;
        } else {
          clearErrors('username');
        }
      });
    }
  };

  const googleLogin = () => {
    dispatch(setIsLogging(true));
    localStorage.setItem('login_type', 'google');
    localStorage.setItem('login_from', 'signup');
    localStorage.setItem('klic_previous_url', window.location.href);
    socialAuthService('Google');
  };

  const discordLogin = () => {
    dispatch(setIsLogging(true));
    localStorage.setItem('login_type', 'discord');
    localStorage.setItem('login_from', 'signup');
    socialAuthService('Discord');
  };

  const twitterLogin = () => {
    dispatch(setIsLogging(true));
    localStorage.setItem('login_type', 'twitter');
    localStorage.setItem('login_from', 'signup');
    socialAuthService('Twitter');
  };

  useEffect(() => {
    if (!errors['email'] && dirtyFields['email']) {
      checkEmail(email);
    }
  }, [email, errors, dirtyFields]);
  useEffect(() => {
    if (!errors['termsOfUse'] && dirtyFields['termsOfUse']) {
      clearErrors('termsOfUse');
    }
  }, [termsOfUse, errors, dirtyFields]);

  useEffect(() => {
    if (!errors['username']) {
      checkUserName(username);
    }
  }, [username, errors, dirtyFields]);

  const searchInput = () => (
    <KlicInputAutocomplete
      id={'search'}
      value={searchValue}
      type={'text'}
      name={'search'}
      label={''}
      placeholder={'Select Country'}
      onChange={(e) => {
        setSearchValue(e.target.value);
        searchCountries(e.target.value);
      }}
      error={null}
      icon={<Icon width={20} icon="searchIcon" />}
      className={style['search-input']}
      isSearch={true}
    />
  );

  const openTermsdModal = () => {
    setIsTermsModalOpen(true);
  };

  const signUp = async () => {
    setBirthdayMessage('');
    const values = await getValues();
    let isValidForm = true;
    if (!getValues('username') || getValues('username') === '') {
      setError('username', { message: 'Please enter username' });
      isValidForm = false;
    }
    if (!getValues('email') || getValues('email') === '') {
      setError('email', { message: 'Please enter email address' });
      isValidForm = false;
    }
    if (!getValues('confirmpassword') || getValues('confirmpassword') === '') {
      setError('confirmpassword', { message: 'Please enter confirm password' });
      isValidForm = false;
    }
    if (!getValues('password') || getValues('password') === '') {
      setError('password', { message: 'Please enter password' });
      isValidForm = false;
    }
    if (!isValid || !getValues('termsOfUse')) {
      setError('termsOfUse', { message: 'Please Accept Terms' });
      isValidForm = false;
      // return;
    }
    if (!getValues('country') || getValues('country') === '') {
      setError('country', { message: 'Please select country' });
      isValidForm = false;
      // return;
    }

    if (selectedMonth && selectedMonth.length === 0) {
      setBirthdayMessage('Please Select Valid Birthdate');
      // return;
      isValidForm = false;
    }
    if (selectedDay && selectedDay.length === 0) {
      setBirthdayMessage('Please Select Valid Birthdate');
      // return;
      isValidForm = false;
    }
    if (selectedYear && selectedYear.length === 0) {
      setBirthdayMessage('Please Select Valid Birthdate');
      // return;
      isValidForm = false;
    }

    if (
      selectedMonth &&
      selectedDay &&
      selectedYear &&
      selectedYear.value >= new Date().getFullYear() - 13
    ) {
      setBirthdayMessage('You Must Be At Least 13 Years or Older to Register');
      return;
    } else if (isValidForm) {
      setBirthdayMessage('');
      const selectedDate = `${selectedMonth.value}/${selectedDay.value}/${selectedYear.value}`;
      setBirthDate(selectedDate);
      setValue('birthdate', selectedDate, {
        shouldDirty: true,
      });
    }
    if (!isValidForm) {
      return;
    }
    await new Promise((resolve, reject) => {
      handleSubmit(
        (data: any) => {
          signUpService(
            username,
            password,
            email,
            country,
            birthday,
            getValues('termsOfUse')
          )
            .then(() => {
              setEmail(email);
              setUsername(username);
              setPassword(password);
              setCountry(country);
              setBirthDate(birthday);
              setMode('verify');
              // setTimeout(() => {
              //   setIsLoading(false);
              //   dispatch(setIsLogging(false));
              // }, 60000);
            })
            .catch((error) => {
              if (error?.name == 'UsernameExistsException') {
                setFieldError('username', {
                  message: 'Username is Already Taken!',
                });
              }
            });
        },
        (err) => {
          setIsLoading(false);
          dispatch(setIsLogging(false));
          reject(err);
        }
      )();
      setIsLoading(false);
      dispatch(setIsLogging(false));
    });
  };

  const closeTermsdModal = () => {
    setIsTermsModalOpen(false);
  };
  const [isPasswordInfoOpen, setIsPasswordInfoOpen] = useState(false);
  const togglePasswordInfo = () => {
    setIsPasswordInfoOpen((prev) => !prev);
  };
  const closePasswordInfo = () => {
    setIsPasswordInfoOpen(false);
  };
  return (
    <div className={style['sign-up-modal__container']}>
      <TermsAndConditionsModal
        onClose={closeTermsdModal}
        isOpen={isTermsModalOpen}
      />
      <div className={style['sign-up-modal__form']}>
        <ControlledInput
          id={'username'}
          placeholder={'User Name'}
          name={'username'}
          required={true}
          onBlur={(e) => {
            checkUserName(e.target.value);
          }}
        />
        <ControlledInput
          id={'email'}
          placeholder={'Email'}
          name={'email'}
          required={true}
        />
        <div className={style['sign-up-modal__password-info']}>
          <ControlledInput
            id={'password'}
            type={'password'}
            placeholder={'Password'}
            name={'password'}
            required={true}
          />
          <div
            className={style['sign-up-modal__password-info--info']}
            onClick={togglePasswordInfo}
          >
            <Icon width={14} icon="infoIcon" />
            <span>Info</span>
          </div>
        </div>
        <ControlledInput
          id={'confirmpassword'}
          type={'password'}
          placeholder={'Confirm Password'}
          name={'confirmpassword'}
          required={true}
        />
        <ControlledSelect
          label={'Select Country'}
          name={'country'}
          options={countries}
          id={'country'}
          header={searchInput()}
          showValue={true}
          required={true}
        />
        <div className={style['birthdate_title']}>
          Birthdate <span className={style['required']}>*</span>
        </div>
        <div className={style['birthdate']}>
          <Select
            id="month"
            name="month"
            placeholder="Month"
            options={MonthList}
            isSearchable={!isMobile ? true : false}
            onChange={(option: any) => {
              setSelectedMonth(option);
              setBirthdayMessage('');
            }}
            value={selectedMonth}
            styles={SelectStyles}
            className={style['month']}
          />
          <Select
            id="day"
            name="day"
            placeholder="Day"
            options={DayList}
            isSearchable={!isMobile ? true : false}
            onChange={(option: any) => {
              setSelectedDay(option);
              setBirthdayMessage('');
            }}
            value={selectedDay}
            styles={SelectStyles}
            className={style['date']}
          />
          <Select
            id="year"
            name="year"
            placeholder="Year"
            options={yearList ?? []}
            isSearchable={!isMobile ? true : false}
            onChange={(option: any) => {
              setSelectedYear(option);
              setBirthdayMessage('');
            }}
            value={selectedYear}
            styles={SelectStyles}
            className={style['year']}
          />
        </div>
        <div className={style['birtherror']}>
          {birthdayMessage !== '' && birthdayMessage}
        </div>
        <div className={style['terms-text']}>
          <ControlledCheckbox name={'termsOfUse'} />
          <div>
            I Accept the KLıC{' '}
            <Link
              className={style['terms-text__link']}
              href={'/terms-of-use'}
              target="_blank"
            >
              Terms of Use
            </Link>
          </div>
        </div>
        <div className={style['rightbtn']}>
          <Button
            type={'secondary-v2'}
            loading={isLoading}
            label={'Submit'}
            onClick={() => signUp()}
            // disabled={!isValid}
            className="btnRight"
          />
        </div>

        <div className={style['sign-up-modal__auth_separator']}>
          <div className={style['sign-up-modal__auth_separator__left']}></div>
          <div className={style['sign-up-modal__auth_separator__center']}>
            Sign Up With
          </div>
          <div className={style['sign-up-modal__auth_separator__right']}></div>
        </div>
        <div className={style['sign-up-modal__social']}>
          <div>
            <Icon
              icon={'googleIcon'}
              width={30}
              height={30}
              onClick={() => {
                googleLogin();
              }}
            />
          </div>
          {(process.env.NEXT_PUBLIC_SOCIAL_LOGIN || 'false') === 'true' && (
            <>
              <div>
                <Icon
                  icon={'discord'}
                  width={30}
                  height={30}
                  onClick={() => {
                    discordLogin();
                  }}
                />
              </div>
              <div>
                <Icon
                  icon={'footerTwitter'}
                  width={30}
                  height={30}
                  onClick={() => {
                    twitterLogin();
                  }}
                />
              </div>
            </>
          )}
        </div>

        {isPasswordInfoOpen && (
          <div
            className={
              style['sign-up-modal__password-info--password-info-details']
            }
          >
            <div
              className={
                style[
                  'sign-up-modal__password-info--password-info-details--header'
                ]
              }
            >
              Password rules
            </div>
            <div
              className={
                style[
                  'sign-up-modal__password-info--password-info-details--body'
                ]
              }
            >
              <ul>
                <li>Must contain 8 or more characters</li>
                <li>
                  Must contain 1 uppercase letter{' '}
                  <span>( eg: A,B,C etc... )</span>
                </li>
                <li>
                  Must contain 1 number <span>( eg: 1,2,3 etc... )</span>
                </li>
                <li>
                  Must contain 1 special character{' '}
                  <span>( eg: @,#,$,% etc... )</span>
                </li>
              </ul>
            </div>
            <div
              className={
                style[
                  'sign-up-modal__password-info--password-info-details--footer'
                ]
              }
            >
              <Button
                onClick={closePasswordInfo}
                type="primary-v2"
                label={'Okay'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SignUpModal: FC<Props> = ({ onClose }) => {
  return (
    <ControlledForm schema={SignUpSchema}>
      <SignUpForm onClose={onClose} />
    </ControlledForm>
  );
};

export default SignUpModal;
